import React, { useState } from "react";
import { Box, Dialog, IconButton, Typography, useTheme } from "@mui/material";
import { Plus, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import InputCst from "@components/InputCst/InputCst";
import { UogItem } from "../types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

interface UogSectionProps {
  value: UogItem[];
  onChange: (next: UogItem[]) => void;
}

const UogSection: React.FC<UogSectionProps> = ({ value, onChange }) => {
  const { t } = useTranslation("user-wizard-v2");
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [uog, setUog] = useState("");
  const [description, setDescription] = useState("");

  const closeModal = () => {
    setOpen(false);
    setUog("");
    setDescription("");
  };

  const handleAdd = () => {
    if (!uog.trim()) return;
    onChange([
      ...value,
      { uog: uog.trim(), uog_description: description.trim() },
    ]);
    closeModal();
  };

  const handleDelete = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            {t("uog.title")}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.palette.text.primary,
              mt: 0.5,
            }}
          >
            {t("uog.note")}
          </Typography>
        </Box>
        <ButtonTertiary
          size="md"
          endIcon={<Plus size={16} />}
          onClick={() => setOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          {t("uog.add")}
        </ButtonTertiary>
      </Box>

      {value.map((item, index) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            backgroundColor: theme.palette.background.card || "#2A2A2A",
            borderRadius: "12px",
            padding: "16px 20px",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
              {item.uog}
            </Typography>
            {item.uog_description && (
              <Typography
                sx={{ fontSize: "14px", color: theme.palette.text.primary }}
              >
                {item.uog_description}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => handleDelete(index)}
            sx={{ color: theme.palette.primary.main }}
            aria-label={t("uog.delete") || "delete"}
          >
            <Trash2 size={18} />
          </IconButton>
        </Box>
      ))}

      <Dialog
        open={open}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "12px",
            p: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography sx={{ fontSize: "22px", fontWeight: 700 }}>
            {t("uog.modalTitle")}
          </Typography>
          <IconButton
            onClick={closeModal}
            sx={{ color: theme.palette.text.primary }}
          >
            <X size={20} />
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <InputCst
              id="uog-name"
              label={t("uog.fieldName")}
              value={uog}
              onChange={(e: any) => setUog(e.target.value)}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <InputCst
              id="uog-description"
              label={t("uog.fieldDescription")}
              value={description}
              onChange={(e: any) => setDescription(e.target.value)}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <ButtonPrimary size="md" onClick={handleAdd} disabled={!uog.trim()}>
            {t("uog.add")}
          </ButtonPrimary>
        </Box>
      </Dialog>
    </Box>
  );
};

export default UogSection;
